// Stimulus controller for collection filter interactions.
// Intercepts form submission to build clean, SEO-friendly URLs with comma-separated params.
import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    connect() {
        this.activePage = this.#currentPage()
        this.ignoreScrollUntil = 0
        this.scrollFrame = null
        this.restoreTimer = null

        this.handleScroll = this.#handleScroll.bind(this)
        this.handleMutations = this.#handleMutations.bind(this)
        this.handlePopState = this.#handlePopState.bind(this)
        this.handleFrameLoad = this.#handleFrameLoad.bind(this)
        this.mutationObserver = new MutationObserver(this.handleMutations)
        this.mutationObserver.observe(this.element, {childList: true, subtree: true})

        window.addEventListener("scroll", this.handleScroll, {passive: true})
        window.addEventListener("resize", this.handleScroll, {passive: true})
        window.addEventListener("popstate", this.handlePopState)
        this.element.addEventListener("turbo:frame-load", this.handleFrameLoad)
        this.#syncPageMarkers()
    }

    disconnect() {
        this.mutationObserver?.disconnect()
        window.removeEventListener("scroll", this.handleScroll)
        window.removeEventListener("resize", this.handleScroll)
        window.removeEventListener("popstate", this.handlePopState)
        this.element.removeEventListener("turbo:frame-load", this.handleFrameLoad)

        if (this.scrollFrame) {
            cancelAnimationFrame(this.scrollFrame)
        }

        if (this.restoreTimer) {
            clearTimeout(this.restoreTimer)
        }
    }

    // Triggered when any filter checkbox or sort select changes. The filter panel
    // lives outside the collection_list frame, so it stays open (with its badges
    // synced here) while the grid refreshes behind it.
    filterChange(event) {
        this.#syncFilterUI()
        this.#submitFilters()
    }

    // Triggered when a selected filter chip is clicked to remove it
    removeFilter(event) {
        event.preventDefault()
        const tag = event.currentTarget.dataset.tag
        if (!tag) return

        const checkbox = document.getElementById(tag)
        if (checkbox) checkbox.checked = false

        this.#syncFilterUI()
        this.#submitFilters()
    }

    // Triggered by the Clear links (drawer header and chip row). The panel is
    // static, so its checkboxes must be reset client-side, not by a re-render.
    clearFilters(event) {
        event.preventDefault()

        this.element.querySelectorAll('.collection-filter-menu input:checked').forEach(checkbox => {
            checkbox.checked = false
        })

        const sortSelect = this.element.querySelector('select[name="sort_order"]')
        if (sortSelect) sortSelect.value = 'best_sellers'

        this.#syncFilterUI()
        this.#submitFilters()
    }

    // Builds clean comma-separated URL params and updates the collection_list turbo frame.
    // Produces: ?tags=celtic,braided&metals=14K&sort=price_asc
    // Instead of: ?tags%5B%5D=113&tags%5B%5D=104&metals%5B%5D=14K&sort_order=best_sellers
    // Uses manual string building (not URLSearchParams) to avoid %2C encoding of commas.
    // The panel and sort control both live under this controller's element.
    #submitFilters() {
        const parts = []

        const filterGroups = { 'tags[]': 'tags', 'p_tags[]': 'p_tags', 'metals[]': 'metals' }
        for (const [inputName, paramName] of Object.entries(filterGroups)) {
            const checked = this.element.querySelectorAll(`input[name="${inputName}"]:checked`)
            if (checked.length > 0) {
                const values = Array.from(checked).map(cb => cb.value).join(',')
                parts.push(`${paramName}=${values}`)
            }
        }

        const sortSelect = this.element.querySelector('select[name="sort_order"]')
        if (sortSelect && sortSelect.value && sortSelect.value !== 'best_sellers') {
            const sortMap = { 'best_sellers': 'best', 'price_asc': 'price_asc', 'price_desc': 'price_desc' }
            parts.push(`sort=${sortMap[sortSelect.value] || sortSelect.value}`)
        }

        const baseUrl = window.location.pathname
        const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl

        Turbo.visit(url, { frame: "collection_list", action: "advance" })
    }

    // The static panel's badges, is-active states, and Clear link reflect the
    // checkbox state, which only this controller changes.
    #syncFilterUI() {
        let total = 0

        this.element.querySelectorAll('.collection-filter-group').forEach(group => {
            const summary = group.querySelector('summary')
            const count = group.querySelectorAll('.collection-filter-menu input:checked').length
            total += count

            summary.classList.toggle('is-active', count > 0)
            let badge = summary.querySelector('.collection-filter-badge')
            if (count > 0) {
                if (!badge) {
                    badge = document.createElement('span')
                    badge.className = 'collection-filter-badge'
                    summary.appendChild(badge)
                }
                badge.textContent = count
            } else {
                badge?.remove()
            }
        })

        document.getElementById('collectionFilterClear')?.classList.toggle('d-none', total === 0)
    }

    // Mirrors fresh server-rendered data into the static panel after each frame
    // refresh: the drawer's count line, results button, and per-option facets.
    #handleFrameLoad(event) {
        if (event.target.id !== 'collection_list') return

        const count = this.element.querySelector('.collection-sort-count')
        if (count) {
            const button = document.getElementById('collectionShowResults')
            if (button) button.textContent = `Show ${count.textContent.trim()}`

            const drawerCount = document.getElementById('collectionDrawerCount')
            if (drawerCount) drawerCount.textContent = count.textContent.trim()
        }

        const facets = event.target.querySelector('[data-collection-facets]')
        if (facets) this.#applyFacetCounts(JSON.parse(facets.textContent))
    }

    // The server recomputes option counts within the filtered set on every
    // response; mirror them into the panel (hiding zero-result options) without
    // re-rendering it, so open menus and scroll position survive. Checked
    // options stay visible regardless so they can always be unchecked.
    #applyFacetCounts(counts) {
        this.element.querySelectorAll('.collection-filter-group').forEach(group => {
            let visible = 0

            group.querySelectorAll('.collection-filter-menu input[type="checkbox"]').forEach(input => {
                const option = input.closest('.form-check')
                const label = option?.querySelector('.form-check-label')
                const count = counts[input.id]
                const show = count !== undefined || input.checked

                option?.classList.toggle('d-none', !show)
                if (show) visible += 1
                if (label && count !== undefined) {
                    label.textContent = label.textContent.replace(/\(\d+\)\s*$/, `(${count})`)
                }
            })

            group.classList.toggle('d-none', visible === 0)
        })
    }

    #handleMutations() {
        this.#syncPageMarkers()
    }

    #syncPageMarkers() {
        this.pageMarkers = Array.from(
            this.element.querySelectorAll("[data-collection-page-url]")
        )
        this.#schedulePageUrlUpdate()
    }

    #handleScroll() {
        this.#schedulePageUrlUpdate()
    }

    #handlePopState() {
        this.activePage = this.#currentPage()
        this.ignoreScrollUntil = Date.now() + 500

        if (this.restoreTimer) {
            clearTimeout(this.restoreTimer)
        }

        this.restoreTimer = setTimeout(() => {
            this.ignoreScrollUntil = 0
            this.#schedulePageUrlUpdate()
        }, 500)
    }

    #schedulePageUrlUpdate() {
        if (this.scrollFrame) return

        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null
            this.#updatePageUrl()
        })
    }

    #updatePageUrl() {
        if (!this.pageMarkers?.length || Date.now() < this.ignoreScrollUntil) return

        const activationLine = window.innerHeight * 0.35
        let activeMarker = this.pageMarkers[0]

        for (const marker of this.pageMarkers) {
            if (marker.getBoundingClientRect().top <= activationLine) {
                activeMarker = marker
            } else {
                break
            }
        }

        const url = activeMarker.dataset.collectionPageUrl
        const page = Number(activeMarker.dataset.collectionPage)
        if (!url || page === this.activePage) return

        const state = {
            ...(window.history.state || {}),
            collectionPage: page
        }

        window.history.pushState(state, "", url)
        this.activePage = page
    }

    #currentPage() {
        const page = Number(new URLSearchParams(window.location.search).get("page"))
        return page > 1 ? page : 1
    }
}
